import { useState, useEffect } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useChat } from '../contexts/ChatContext'
import { getCallHistory } from '../services/api'
import { Row, EmptyState, Button } from '@ofself/shell'

// A huddle's relative age ("3 months ago", "5 minutes ago") — Slack's huddle-list language.
function ago(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  if (isNaN(d.getTime())) return ''
  return formatDistanceToNow(d, { addSuffix: true })
}

// A call's spoken duration ("10 minutes", "1m 5s", "45s"). Null when it never recorded one
// (a missed / declined invite).
function durationLabel(seconds) {
  if (seconds == null) return ''
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m >= 60) { const h = Math.floor(m / 60); return `${h}h ${m % 60}m` }
  if (m) return s ? `${m}m ${s}s` : `${m} minute${m === 1 ? '' : 's'}`
  return `${s}s`
}

function HeadphonesIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  )
}
function PhoneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

// HUDDLES — the cross-channel call history (and any live huddles), the standalone Huddles page.
// Reads the SAME data the Activity panel's Calls filter does (getCallHistory + the live activeHuddles
// map), presented as a dedicated, Slack-style "Recent huddles" list. Clicking a row opens its channel;
// a live huddle offers a one-tap Join.
export default function HuddlesPage() {
  const { channels, dmChannels, activeHuddles, selectChannel, startHuddle, activeChannel } = useChat()
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    getCallHistory()
      .then(res => { if (alive) setHistory(res.calls || []) })
      .catch(err => console.error('Failed to fetch huddle history:', err))
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const allChannels = [...(channels || []), ...(dmChannels || [])]
  const channelFor = (id) => allChannels.find(c => c.id === id)
  const activeList = Object.values(activeHuddles || {})
  const activeChannelIds = new Set(activeList.map(c => c.channel_id || c.huddle?.channel_id))

  // The display name for a call/huddle — the DM partner for a direct call, else the #channel.
  function titleFor({ is_dm, channel_id, channel_name }) {
    const ch = channelFor(channel_id)
    if (is_dm) return ch?.other_username || channel_name || 'Direct message'
    return `#${channel_name || channel_id?.slice(0, 8) || 'unknown'}`
  }
  function participantsLabel(parts) {
    const names = (parts || []).map(p => (typeof p === 'object' ? (p.username || p.user_id?.slice(0, 6)) : p)).filter(Boolean)
    return names.join(', ')
  }

  // "Start a huddle" with no channel context → start in the active channel, else the first available
  // channel/DM. The huddle's fullscreen view is the app-root <HuddleBar/> (shows for any surface).
  function onStartHuddle() {
    const ch = activeChannel || allChannels[0]
    if (ch) { selectChannel(ch); startHuddle?.() }
  }

  return (
    <div className="sidebar huddles-page">
      {/* The standing way in once there is a list; an empty page's own
          state carries the one CTA instead. */}
      {history.length + activeList.length > 0 && (
        <div className="huddles-hero">
          <button type="button" className="huddles-hero-cta" onClick={onStartHuddle}>
            <HeadphonesIcon /> Start a huddle
          </button>
        </div>
      )}
      <ul className="block__body block-scroll list-rows huddles-list" style={{ padding: '8px 0 0' }}>
        {/* LIVE — huddles happening right now, joinable in one tap. */}
        {activeList.length > 0 && (
          <>
            <li className="huddles-section-label">Active now</li>
            {activeList.map(call => {
              const channelId = call.channel_id || call.huddle?.channel_id
              const ch = channelFor(channelId)
              const name = ch?.is_dm ? (ch.other_username || call.channel_name) : `#${call.channel_name || channelId?.slice(0, 8)}`
              const parts = call.huddle?.participants || []
              const names = parts.map(p => p.username || p.user_id?.slice(0, 6)).join(', ')
              return (
                <Row as="li" key={`live-${channelId}`} className="list-row huddle-row" onClick={() => ch && selectChannel(ch)}>
                  <div className="activity-icon-wrap huddle-live" aria-hidden="true">
                    <span className="huddle-live-dot" />
                  </div>
                  <span className="list-row__body">
                    <span className="list-row__top">
                      <span className="list-row__name"><strong>{name}</strong><span className="huddle-live-tag">Live</span></span>
                    </span>
                    <span className="list-row__preview">{names || 'No one here yet'}</span>
                  </span>
                  <button
                    type="button"
                    className="huddle-join-btn"
                    onClick={e => { e.stopPropagation(); if (ch) { selectChannel(ch); startHuddle?.() } }}
                  >
                    Join
                  </button>
                </Row>
              )
            })}
          </>
        )}

        {/* PAST — recent call history. */}
        {!loading && history.length > 0 && (
          <li className="huddles-section-label">Recent huddles</li>
        )}
        {loading ? (
          <EmptyState as="li">Loading huddles…</EmptyState>
        ) : history.length === 0 && activeList.length === 0 ? (
          <EmptyState as="li" action={<Button onClick={onStartHuddle}>Start a huddle</Button>}>
            No huddles yet
          </EmptyState>
        ) : (
          history.map((call, i) => {
            const ch = channelFor(call.channel_id)
            const missed = call.duration_seconds == null
            const dur = durationLabel(call.duration_seconds)
            const live = activeChannelIds.has(call.channel_id)
            if (live) return null   // already shown in Active now
            const parts = participantsLabel(call.participants)
            return (
              <Row
                as="li"
                key={call.id || i}
                className="list-row huddle-row"
                onClick={() => ch && selectChannel(ch)}
              >
                <div className={`activity-icon-wrap huddle-icon${missed ? ' huddle-missed' : ''}`} aria-hidden="true">
                  {missed ? <PhoneIcon /> : <HeadphonesIcon />}
                </div>
                <span className="list-row__body">
                  <span className="list-row__top">
                    <span className="list-row__name"><strong>{titleFor(call)}</strong></span>
                    <span className="list-row__time">{ago(call.ended_at || call.started_at)}</span>
                  </span>
                  <span className="list-row__preview">
                    {missed ? 'Missed huddle' : dur}
                    {!missed && parts ? <span className="huddle-with"> · {parts}</span> : null}
                    {missed && parts ? <span className="huddle-with"> · {parts}</span> : null}
                  </span>
                </span>
              </Row>
            )
          })
        )}
      </ul>
    </div>
  )
}
